import {Matrix4,Vector3} from 'three';

type Raster={width:number;height:number;data:ArrayLike<number>};
export type Baseboard={height:number;color:[number,number,number]};
const median=(values:number[])=>values.sort((a,b)=>a-b)[Math.floor(values.length/2)];
const difference=(a:number[],b:number[])=>Math.sqrt(a.reduce((sum,v,c)=>sum+(v-b[c])**2,0)/3);
/** Follow the calibrated wall/floor junction, sampling vertical profiles rather
 * than stretching screen-space lines. Require repeated, matching trim evidence
 * so an outlet or isolated leftover object cannot become a room-wide stripe. */
export function detectRoomBaseboards(image:Raster,planes:Vector3[][],projection:Matrix4):(Baseboard|null)[]{
 const {width,height,data}=image;
 return planes.map((points,id)=>{
  if(id>1)return null;
  const origin=points[0],along=points[id===0?1:3].clone().sub(origin).normalize();
  const up=points[id===0?3:1].clone().sub(origin).normalize();
  function sample(distance:number,elevation:number){
   const world=origin.clone().addScaledVector(along,distance).addScaledVector(up,elevation);
   if(world.z>=-.05)return null;
   const p=world.applyMatrix4(projection),x=Math.floor((p.x*.5+.5)*width),y=Math.floor((.5-p.y*.5)*height);
   if(x<2||x>=width-2||y<2||y>=height-2)return null;
   const at=(y*width+x)*4;return [data[at],data[at+1],data[at+2]];
  }
  const candidates:{distance:number;height:number;color:number[]}[]=[];
  for(let distance=.15;distance<12;distance+=.1){
   const wall=sample(distance,.35),bottom=sample(distance,.025);
   if(!wall||!bottom||difference(wall,bottom)<25)continue;
   let top=0;const band:number[][]=[];
   for(let elevation=.025;elevation<=.255;elevation+=.005){
    const color=sample(distance,elevation);
    if(!color||difference(color,bottom)>22||difference(color,wall)<18)break;
    top=elevation;band.push(color);
   }
   // Typical architectural trim, with a distinct return to wall above it.
   const above=sample(distance,top+.025);
   if(top<.045||top>.23||!above||difference(above,wall)>20)continue;
   candidates.push({distance,height:top+.0025,color:[0,1,2].map(c=>median(band.map(p=>p[c])))});
  }
  let best:typeof candidates=[];
  for(const seed of candidates){
   const matching=candidates.filter(p=>Math.abs(p.height-seed.height)<.025&&difference(p.color,seed.color)<20);
   if(matching.length>best.length)best=matching;
  }
  if(best.length<6||best[best.length-1].distance-best[0].distance<.6)return null;
  return {height:median(best.map(p=>p.height)),color:[0,1,2].map(c=>median(best.map(p=>p.color[c]))/255) as [number,number,number]};
 });
}
